/* Noxrivet collection page (sections/nxc-collection.liquid).
   Progressive enhancement over plain links and a GET form: filters, sort and filter links update the
   [data-nxc-part] regions through the Section Rendering API and the address bar (shareable, back/forward);
   numbered page links swap in that page and move to the top of the grid; filter and sort changes return
   to page 1; below 990px the sidebar becomes a modal drawer. */
(() => {
  const PARTS = ['count', 'types', 'clear', 'filters', 'foot', 'filter-count', 'sort', 'chips', 'results', 'pager'];
  const OWN = (k) => k.startsWith('filter.') || k === 'sort_by' || k === 'page' || k === 'section_id' || k === 'options';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([hidden]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class NxcCollection {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.form = root.querySelector('[data-nxc-form]');
      this.drawer = root.querySelector('[data-nxc-drawer]');
      this.results = root.querySelector('[data-nxc-results]');
      this.status = root.querySelector('[data-nxc-status]');
      this.cache = new Map();
      this.token = 0;
      this.mq = window.matchMedia('(max-width: 989px)');
      this.onKey = this.onKey.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);

      root.addEventListener('click', (e) => this.onClick(e));
      root.addEventListener('change', (e) => this.onChange(e));
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.apply();
      });
      this.mq.addEventListener?.('change', () => {
        if (!this.mq.matches) this.closeDrawer(false);
        this.fitSort();
      });
      window.addEventListener('popstate', (e) => {
        if (!e.state || !e.state.nxc) return;
        this.update(new URLSearchParams(location.search), { push: false, keepPage: true, y: e.state.y });
      });
      history.replaceState({ ...(history.state || {}), nxc: true }, '', location.href);
      /* remember where the shopper is on each page so back/forward between pages lands there */
      window.addEventListener(
        'scroll',
        () => {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => {
            if (history.state?.nxc) history.replaceState({ ...history.state, y: window.scrollY }, '', location.href);
          }, 150);
        },
        { passive: true }
      );
      this.fitSort();
      root.querySelectorAll('[data-nxc-tabs]').forEach((nav) => this.revealTabs(nav));
    }

    /* ---------- events ---------- */

    onClick(e) {
      const toggle = e.target.closest('[data-nxc-toggle]');
      if (toggle) {
        this.setGroup(toggle, toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }
      const more = e.target.closest('[data-nxc-showmore]');
      if (more) {
        this.setMore(more, more.getAttribute('aria-expanded') !== 'true');
        return;
      }
      const link = e.target.closest('a[data-nxc-link]');
      if (link && !this.modified(e)) {
        e.preventDefault();
        this.followLink(link);
        return;
      }
      const page = e.target.closest('a[data-nxc-page]');
      if (page && !this.modified(e)) {
        e.preventDefault();
        const p = new URL(page.href, location.origin).searchParams;
        this.update(this.withExtras(p), { keepPage: true, toGrid: true });
        return;
      }
      if (e.target.closest('a[data-nxc-top]') && !this.modified(e)) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: this.motion() });
        document.getElementById('MainContent')?.focus({ preventScroll: true });
        return;
      }
      const opener = e.target.closest('[data-nxc-open]');
      if (opener) {
        this.openDrawer(opener);
        return;
      }
      if (e.target.closest('[data-nxc-close]')) this.closeDrawer(true);
    }

    onChange(e) {
      const t = e.target;
      if (!this.form || t.form !== this.form) return;
      if (t.matches('[data-nxc-primary]')) {
        this.form.querySelectorAll(`[data-nxc-twin="${t.id}"]`).forEach((twin) => (twin.checked = t.checked));
      }
      if (t.matches('[data-nxc-sort]')) this.fitSort();
      this.apply();
    }

    modified(e) {
      return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0;
    }

    motion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    /* ---------- state ---------- */

    formParams() {
      this.normalizePrice();
      const p = new URLSearchParams();
      for (const [k, v] of new FormData(this.form)) {
        if (typeof v === 'string' && v.trim() !== '') p.append(k, v.trim());
      }
      const sort = this.root.querySelector('[data-nxc-sort]');
      if (sort && p.get('sort_by') === sort.dataset.default) p.delete('sort_by');
      return p;
    }

    /* keep parameters this page does not own (e.g. a theme preview) */
    withExtras(p) {
      for (const [k, v] of new URLSearchParams(location.search)) {
        if (!OWN(k) && !p.has(k)) p.append(k, v);
      }
      return p;
    }

    normalizePrice() {
      this.form.querySelectorAll('[data-nxc-price]').forEach((box) => {
        const [lo, hi] = box.querySelectorAll('input');
        const read = (i) => {
          const n = parseFloat(String(i.value).replace(',', '.'));
          return Number.isFinite(n) ? Math.max(0, n) : null;
        };
        let a = read(lo);
        let b = read(hi);
        if (a !== null && b !== null && a > b) [a, b] = [b, a];
        lo.value = a === null ? '' : String(a);
        hi.value = b === null ? '' : String(b);
      });
    }

    apply() {
      this.update(this.withExtras(this.formParams()));
    }

    followLink(a) {
      let p;
      if (a.dataset.nxcRemove) {
        p = new URLSearchParams(location.search);
        let values = [];
        try {
          values = JSON.parse(a.dataset.nxcValues || '[]');
        } catch (err) {
          values = [];
        }
        const name = a.dataset.nxcRemove;
        const keep = p.getAll(name).filter((v) => !values.includes(v));
        p.delete(name);
        keep.forEach((v) => p.append(name, v));
      } else {
        p = new URL(a.href, location.origin).searchParams;
      }
      p.delete('page');
      p.delete('section_id');
      this.update(this.withExtras(p));
    }

    /* keepPage: page links and back/forward; everything else (filters, sort) returns to page 1 */
    async update(params, { push = true, keepPage = false, toGrid = false, y } = {}) {
      if (!keepPage || params.get('page') === '1') params.delete('page');
      params.delete('section_id');
      const qs = params.toString();
      const url = location.pathname + (qs ? `?${qs}` : '');
      const fetchUrl = `${location.pathname}?${qs ? `${qs}&` : ''}section_id=${encodeURIComponent(this.sectionId)}`;
      const token = ++this.token;
      this.ctrl?.abort();
      this.ctrl = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      try {
        let html = this.cache.get(fetchUrl);
        if (!html) {
          const res = await fetch(fetchUrl, { signal: this.ctrl.signal, credentials: 'same-origin' });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          html = await res.text();
          this.cache.set(fetchUrl, html);
        }
        if (token !== this.token) return;
        if (push) {
          history.replaceState({ ...(history.state || {}), nxc: true, y: window.scrollY }, '', location.href);
          history.pushState({ nxc: true }, '', url);
        }
        this.render(html);
        const grid = this.root.querySelector('[data-nxc-grid]');
        if (toGrid) this.toGrid();
        else if (typeof y === 'number') window.scrollTo(0, y);
        if (keepPage && grid?.dataset.pages > 1) {
          this.say(this.root.dataset.pageOf.replace('[page]', grid.dataset.page).replace('[pages]', grid.dataset.pages));
        } else {
          this.say(this.root.dataset.results.replace('[count]', grid ? grid.dataset.count : '0'));
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        window.location.assign(url);
      } finally {
        if (token === this.token) this.results.removeAttribute('aria-busy');
      }
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-nxc]');
      if (!fresh) throw new Error('Section markup missing');

      const groups = {};
      this.root.querySelectorAll('[data-nxc-toggle]').forEach((b) => {
        groups[b.dataset.nxcToggle] = b.getAttribute('aria-expanded') === 'true';
      });
      const mores = {};
      this.root.querySelectorAll('[data-nxc-group]').forEach((g) => {
        const b = g.querySelector('[data-nxc-showmore]');
        if (b) mores[g.dataset.nxcGroup] = b.getAttribute('aria-expanded') === 'true';
      });
      const active = document.activeElement;
      const activeId = active && this.root.contains(active) ? active.id : '';
      const typeScroll = this.root.querySelector('[data-nxc-types]')?.scrollLeft || 0;

      for (const name of PARTS) {
        const next = fresh.querySelector(`[data-nxc-part="${name}"]`);
        const cur = this.root.querySelector(`[data-nxc-part="${name}"]`);
        if (next && cur) cur.replaceWith(document.importNode(next, true));
      }
      this.cleanLinks(this.root);

      this.root.querySelectorAll('[data-nxc-toggle]').forEach((b) => {
        const k = b.dataset.nxcToggle;
        if (k in groups) this.setGroup(b, groups[k]);
      });
      this.root.querySelectorAll('[data-nxc-group]').forEach((g) => {
        const b = g.querySelector('[data-nxc-showmore]');
        const k = g.dataset.nxcGroup;
        if (b && mores[k] && b.getAttribute('aria-expanded') !== 'true') this.setMore(b, true);
      });

      this.fitSort();
      this.revealTabs(this.root.querySelector('[data-nxc-types]'), typeScroll);
      if (active && !document.contains(active)) {
        const again = activeId && document.getElementById(activeId);
        if (again) again.focus({ preventScroll: true });
        else if (this.drawer.classList.contains('is-open')) this.drawer.querySelector('[data-nxc-panel] .nxc-side__x')?.focus();
        else this.root.querySelector('[data-nxc-heading]')?.focus({ preventScroll: true });
      }
    }

    /* the Section Rendering API can echo section_id into generated links */
    cleanLinks(scope) {
      scope.querySelectorAll('a[href*="section_id="]').forEach((a) => {
        const u = new URL(a.getAttribute('href'), location.origin);
        u.searchParams.delete('section_id');
        a.setAttribute('href', u.pathname + u.search);
      });
    }

    say(text) {
      if (!this.status) return;
      this.status.textContent = '';
      window.setTimeout(() => (this.status.textContent = text), 60);
    }

    /* ---------- accordions ---------- */

    setGroup(btn, open) {
      btn.setAttribute('aria-expanded', String(open));
      const body = document.getElementById(btn.getAttribute('aria-controls'));
      if (body) body.hidden = !open;
    }

    setMore(btn, open) {
      const group = btn.closest('[data-nxc-group]');
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? btn.dataset.less : btn.dataset.more;
      const extras = group.querySelectorAll('[data-nxc-extra]');
      extras.forEach((el) => (el.hidden = !open));
      if (open && extras[0]) extras[0].querySelector('input:not([hidden])')?.focus({ preventScroll: true });
    }

    /* ---------- pages: start of the grid, clear of the sticky header ---------- */

    toGrid() {
      const grid = this.root.querySelector('[data-nxc-part="results"]');
      if (!grid) return;
      /* the sticky header slides back in when the page scrolls up, so leave room for it */
      let offset = 0;
      document.querySelectorAll('.shopify-section-group-header-group').forEach((el) => {
        const pos = getComputedStyle(el).position;
        if (pos === 'sticky' || pos === 'fixed') offset = Math.max(offset, el.offsetHeight);
      });
      const top = grid.getBoundingClientRect().top + window.scrollY - offset - 8;
      window.scrollTo(0, Math.max(0, top));
    }

    /* ---------- drawer ---------- */

    openDrawer(opener) {
      if (!this.mq.matches) return;
      this.opener = opener;
      this.drawer.setAttribute('role', 'dialog');
      this.drawer.setAttribute('aria-modal', 'true');
      this.drawer.classList.add('is-open');
      opener.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('nxc-lock');
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('focusin', this.onFocusIn);
      this.drawer.querySelector('.nxc-side__x')?.focus();
    }

    closeDrawer(restore) {
      if (!this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.removeAttribute('role');
      this.drawer.removeAttribute('aria-modal');
      document.documentElement.classList.remove('nxc-lock');
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('focusin', this.onFocusIn);
      const opener = this.opener || this.root.querySelector('[data-nxc-open]');
      opener?.setAttribute('aria-expanded', 'false');
      if (restore) opener?.focus();
    }

    /* keep focus in the open drawer whatever the browser's tab order (e.g. Safari skipping buttons) */
    onFocusIn(e) {
      const panel = this.drawer.querySelector('[data-nxc-panel]');
      if (!panel.contains(e.target)) panel.querySelector('.nxc-side__x')?.focus();
    }

    onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeDrawer(true);
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = this.drawer.querySelector('[data-nxc-panel]');
      const items = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length || el.classList.contains('nxc-vh'));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!panel.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---------- tab rows: keep the selected tab in view when a row scrolls (mobile) ---------- */

    revealTabs(nav, from = 0) {
      const cur = nav?.querySelector('[aria-current]');
      if (!cur || nav.scrollWidth <= nav.clientWidth) return;
      const fade = 44; /* the scroll-hint mask width in nxc.css */
      const gutter = parseFloat(getComputedStyle(nav).paddingLeft) || 0;
      let left = from;
      if (cur.offsetLeft - gutter < left) left = cur.offsetLeft - gutter;
      else if (cur.offsetLeft + cur.offsetWidth + fade > left + nav.clientWidth) left = cur.offsetLeft + cur.offsetWidth + fade - nav.clientWidth;
      nav.scrollLeft = Math.max(0, Math.min(left, nav.scrollWidth - nav.clientWidth));
    }

    /* ---------- sort: size the select to its selected option on desktop ---------- */

    fitSort() {
      const sel = this.root.querySelector('[data-nxc-sort]');
      if (!sel) return;
      if (this.mq.matches || (window.CSS && CSS.supports('field-sizing', 'content'))) {
        sel.style.width = '';
        return;
      }
      const cs = getComputedStyle(sel);
      const probe = document.createElement('span');
      probe.textContent = sel.options[sel.selectedIndex]?.text || '';
      Object.assign(probe.style, {
        position: 'absolute',
        visibility: 'hidden',
        whiteSpace: 'pre',
        fontFamily: cs.fontFamily,
        fontSize: cs.fontSize,
        fontWeight: cs.fontWeight,
        fontStretch: cs.fontStretch,
        letterSpacing: cs.letterSpacing,
      });
      document.body.appendChild(probe);
      const w = probe.getBoundingClientRect().width;
      probe.remove();
      sel.style.width = `${Math.ceil(w + parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + 6)}px`;
    }
  }

  const init = (root) => {
    if (root && !root.nxc) root.nxc = new NxcCollection(root);
  };
  document.querySelectorAll('[data-nxc]').forEach(init);
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-nxc]').forEach(init));
})();
